import type {ReactNode} from "react";

/**
 * Client-seitiges Treffer-Highlighting für den Live-Freitextfilter der Startseite (Gegenstück zur
 * Backend-Normalisierung in ValuationElasticQueries.normalizeFreeTextVariants): Zelltexte und
 * Suchtoken werden auf eine gemeinsame Normalform abgebildet (lowercase, ß→ss, ä→ae, ö→oe, ü→ue,
 * straße→str am Tokenende in beide Richtungen), dort per Teilstring gesucht und die Fundstellen
 * über eine Index-Rückabbildung im Originaltext markiert.
 *
 * Best effort: Fuzzy-Treffer des Backends (z.B. "alee"→"allee") und Klarnamen-Treffer, die nur
 * über die Account-Registry zustande kamen, während die Zelle den Roh-Usernamen zeigt, werden
 * nicht markiert — die Zeile matcht dann sichtbar ohne Hervorhebung.
 */

/** Ein Zeichen des Originaltexts kann auf 1-2 Zeichen der Normalform abgebildet werden. */
const CHAR_CANON: Record<string, string> = {
    ß: "ss",
    ä: "ae",
    ö: "oe",
    ü: "ue",
};

interface CanonicalText {
    canon: string;
    /** Original-Index je Normalform-Zeichen (Länge === canon.length). */
    origIndex: number[];
}

function canonicalize(text: string): CanonicalText {
    let canon = "";
    const origIndex: number[] = [];
    for (let i = 0; i < text.length; i++) {
        const mapped = CHAR_CANON[text[i].toLowerCase()] ?? text[i].toLowerCase();
        for (const c of mapped) {
            // Doppelbuchstaben-Toleranz (allee==alee, Gegenstück zu den Backend-Varianten): aufeinander-
            // folgende gleiche Buchstaben kollabieren auf beiden Seiten. Nur Buchstaben — Ziffernfolgen
            // (Auftragsnummern) bleiben exakt.
            if (/[a-z]/.test(c) && canon.endsWith(c)) {
                continue;
            }
            canon += c;
            origIndex.push(i);
        }
    }
    return {canon, origIndex};
}

/**
 * Normalform-Varianten eines Suchtokens: die kanonische Form selbst plus die
 * Straßen-Abkürzungs-Äquivalenzen ("...str."/"...str" ↔ "...strasse") in beide Richtungen.
 */
export function canonicalTokenVariants(token: string): string[] {
    // In der Normalform ist "straße"/"strasse" bereits zu "strase" kollabiert.
    const base = canonicalize(token).canon;
    const variants = new Set([base]);
    const stem = base.endsWith("str.") ? base.slice(0, -4) : base.endsWith("str") ? base.slice(0, -3) : null;
    if (stem !== null) {
        variants.add(`${stem}str`);
        variants.add(`${stem}strase`);
    }
    if (base.endsWith("strase")) {
        variants.add(`${base.slice(0, -6)}str`);
    }
    variants.delete("");
    return [...variants];
}

/** Fundstellen (Originaltext-Bereiche, überlappungsfrei zusammengeführt) aller Token-Varianten. */
function matchRanges(text: string, tokens: string[]): Array<[number, number]> {
    const {canon, origIndex} = canonicalize(text);
    const ranges: Array<[number, number]> = [];
    for (const token of tokens) {
        for (const variant of canonicalTokenVariants(token)) {
            let from = 0;
            for (let hit = canon.indexOf(variant, from); hit >= 0; hit = canon.indexOf(variant, from)) {
                // Ende über das NÄCHSTE Normalform-Zeichen abbilden, damit beim Kollabieren übersprungene
                // Doppelbuchstaben mit markiert werden ("alee" soll ganz "allee" markieren, nicht "alle").
                const hitEnd = hit + variant.length;
                ranges.push([origIndex[hit], hitEnd < canon.length ? origIndex[hitEnd] : text.length]);
                from = hit + 1;
            }
        }
    }
    ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const merged: Array<[number, number]> = [];
    for (const [start, end] of ranges) {
        const last = merged[merged.length - 1];
        if (last && start <= last[1]) {
            last[1] = Math.max(last[1], end);
        } else {
            merged.push([start, end]);
        }
    }
    return merged;
}

/**
 * Markiert alle Freitext-Fundstellen in `text` mit <mark>. Ohne aktiven Filter oder ohne
 * Fundstelle kommt der Text unverändert zurück.
 */
export function highlightFreeText(text: string | null | undefined, freeText: string | null | undefined): ReactNode {
    if (text == null || text === "" || freeText == null || freeText.trim() === "") {
        return text;
    }
    const tokens = freeText.trim().split(/\s+/);
    const ranges = matchRanges(text, tokens);
    if (ranges.length === 0) {
        return text;
    }
    const parts: ReactNode[] = [];
    let pos = 0;
    ranges.forEach(([start, end], i) => {
        if (start > pos) {
            parts.push(text.slice(pos, start));
        }
        // Styling (Light/Dark) in PageStart.scss — Tailwinds dark:-Variante ist in REVA deaktiviert.
        parts.push(
            <mark key={i} className="freetextMatch">
                {text.slice(start, end)}
            </mark>,
        );
        pos = end;
    });
    if (pos < text.length) {
        parts.push(text.slice(pos));
    }
    return <>{parts}</>;
}
